import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Button,
  Tooltip
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"; // at top

type Status = {
  orderNumber: string;
  status: "pending" | "done" | "error";
  errorMessage?: any;
};

type Props = {
  open: boolean;
  orderStatuses: Status[];
  title: string;
  onClose: () => void;
  currentlyUpdatingOrder: string | null;
};

const StatusUpdateModal: React.FC<Props> = ({ open, orderStatuses, title, onClose, currentlyUpdatingOrder }) => {
  const allDone = orderStatuses.every((s) => s.status !== "pending");

  const getStatusIcon = (status: Status) => {
    if (status.status === "done") {
      return <CheckIcon color="success" fontSize="small" />;
    } else if (status.status === "error") {
      const tooltipText =
        typeof status.errorMessage === "string" ? status.errorMessage : JSON.stringify(status.errorMessage);
      return (
        <Tooltip title={tooltipText}>
          <ErrorIcon color="error" fontSize="small" />
        </Tooltip>
      );
    } else if (status.orderNumber === currentlyUpdatingOrder) {
      return <CircularProgress size={20} />;
    } else {
      return <HourglassEmptyIcon color="disabled" fontSize="small" />;
    }
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography fontWeight={600} mb={2}>
          Updating Orders
        </Typography>
        <Stack spacing={1}>
          {orderStatuses.map((status) => (
            <Box
              key={status.orderNumber}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#f9f9f9",
                px: 2,
                py: 1.5,
                borderRadius: 2
              }}
            >
              <Typography>{status.orderNumber}</Typography>
              {getStatusIcon(status)}
            </Box>
          ))}
        </Stack>

        {allDone && (
          <Typography
            mt={3}
            color={orderStatuses.some((s) => s.status === "error") ? "warning.main" : "success.main"}
            textAlign="center"
          >
            {orderStatuses.some((s) => s.status === "error")
              ? "Some orders failed. Hover over the error icons to view details."
              : "All orders have been updated successfully."}
          </Typography>
        )}
      </DialogContent>
      {allDone && (
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default StatusUpdateModal;
